import { useState } from "react";
import { Copy, Check, SendHorizonal, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const PHONE = /(?<![\d.,])(?:\+?62|0)?8\d{8,11}(?![\d.,]\d)|(?<!\d)(?:\+62\s?|0)8\d{2,3}(?:[\s-]\d{3,4}){2}(?![\d.,]\d)/g;
const norm = (v) => {
  const d = v.replace(/\D/g, "");
  return d.startsWith("0") ? `62${d.slice(1)}` : d.startsWith("8") ? `62${d}` : d;
};
const MULT = { rb: 1e3, ribu: 1e3, k: 1e3, jt: 1e6, juta: 1e6 };

function parseAmount(rest) {
  const s = rest.match(/(\d+(?:[.,]\d+)?)\s*(rb|ribu|k|jt|juta)\b/i);
  if (s) return String(Math.round(parseFloat(s[1].replace(",", ".")) * MULT[s[2].toLowerCase()]));
  const am = rest.match(/(?:nominal|sebesar|senilai|rp\.?)\s*:?\s*(?:rp\.?\s*)?(\d[\d.,]*)/i) || rest.match(/\b(\d{1,3}(?:[.,]\d{3})+|\d{4,})\b/);
  return am ? am[1].replace(/[.,]\d{2}$/, "").replace(/[.,]/g, "") : "";
}

export function parseTransfer(text, ours = []) {
  const t = text || "";
  const nums = [...t.matchAll(PHONE)].map((m) => ({ raw: m[0], n: norm(m[0]) }));
  const pick = (w) => {
    const m = t.match(new RegExp(`${w}\\s*(?:nomor|no\\.?|nmr|hp)?\\s*:?\\s*(${PHONE.source})`, "i"));
    return m ? norm(m[1]) : "";
  };
  const own = nums.find((x) => ours.includes(x.n))?.n;
  const to = own || pick("\\bke") || (nums.length > 1 ? nums.find((x) => x.n !== pick("dari"))?.n : "") || "";
  const from = (own && nums.find((x) => x.n !== own)?.n) || pick("dari") || nums.find((x) => x.n !== to)?.n || "";
  let rest = t;
  nums.forEach((x) => (rest = rest.replace(x.raw, " ")));
  return { from, to, amount: parseAmount(rest), numbers: [...new Set(nums.map((x) => x.n))] };
}

function Result({ r }) {
  const [copied, setCopied] = useState(false);
  const sim = r.slots[0];
  const line = `${sim?.name || ""}\t\t${r.from}\t${r.amount}`;
  const copy = async () => {
    await navigator.clipboard.writeText(line);
    setCopied(true);
    toast.success("Disalin. Tempel di kolom D WPS");
  };
  return (
    <div data-testid="transfer-result" className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
      <p className="text-sm text-slate-600 whitespace-pre-wrap">{r.text}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <Field label="Nama SIM (tujuan)" testid="transfer-sim">
          {sim ? <span className="font-bold text-emerald-700">{sim.name || "tanpa nama"} {sim.company && <span className="text-xs text-slate-500">{sim.company}</span>}</span> : <span className="text-red-600">tidak ditemukan</span>}
        </Field>
        <Field label="Ke nomor" testid="transfer-to">{r.to || "—"}{sim && <span className="block text-[11px] text-slate-500">{sim.device_name} · {sim.port}.{sim.slot}</span>}</Field>
        <Field label="Dari nomor" testid="transfer-from">{r.from || "—"}</Field>
        <Field label="Nominal" testid="transfer-amount">{r.amount ? Number(r.amount).toLocaleString("id-ID") : "—"}</Field>
      </div>
      {r.slots.length > 1 && <p className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />Nomor ini terdaftar di {r.slots.length} slot: {r.slots.map((s) => `${s.name} (${s.device_name} ${s.port}.${s.slot})`).join(", ")}</p>}
      <div className="flex items-center gap-3">
        <code className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 font-mono">{line.replace(/\t/g, " ⇥ ")}</code>
        <Button data-testid="transfer-copy" size="sm" onClick={copy} disabled={!sim || !r.from || !r.amount}
          className={copied ? "bg-sky-100 text-sky-700 hover:bg-sky-200" : "bg-emerald-500 hover:bg-emerald-600 text-white"}>
          {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}Salin
        </Button>
      </div>
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
      <div data-testid={testid} className="font-mono">{children}</div>
    </div>
  );
}

export default function Transfer() {
  const [text, setText] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    const first = parseTransfer(t);
    if (!first.numbers.length) return toast.error("Tidak ada nomor HP di pesan");
    setBusy(true);
    try {
      const r = await api.get("/slots/by-number", { params: { numbers: first.numbers.join(",") } });
      const ours = r.data.map((s) => s.msisdn || norm(s.number || ""));
      const p = parseTransfer(t, ours);
      setItems([{ ...p, text: t, slots: r.data.filter((s) => (s.msisdn || norm(s.number || "")) === p.to), id: Date.now() }]);
      setText("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="max-w-3xl space-y-5">
      <div>
        <h1 className="font-display text-4xl font-bold">Cek Transfer</h1>
        <p className="text-sm text-slate-500 mt-1">Tempel pesan transfer dari customer lalu tekan Enter. Nomor yang terdaftar di Peta Slot otomatis dianggap nomor penerima (SIM kita), nomor lain = pengirim. Hasil lama diganti yang terbaru.</p>
      </div>
      <div className="relative">
        <Textarea data-testid="transfer-input" rows={3} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
          placeholder="halo kak, aku sudah transfer ya dari nomor 0813... ke nomor 0812... dengan nominal 55.000"
          className="bg-white pr-12" />
        <button data-testid="transfer-submit" onClick={submit} disabled={busy} className="absolute right-3 bottom-3 text-emerald-600 hover:text-emerald-700 disabled:opacity-50">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <SendHorizonal className="h-5 w-5" />}
        </button>
      </div>
      <div className="space-y-3">{items.map((r) => <Result key={r.id} r={r} />)}</div>
    </div>
  );
}
